import React from "react";
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from "react-icons/lu";

import { FilterOption } from "./FilterSelect";
import { TableHead } from "./Table";
import cx from "./cx";
import { tableHeadCellClass } from "./styles";

export type SortOrder = "asc" | "desc";

// As the lists' ક્રમ filter names them.
const orderLabels: Record<SortOrder, string> = {
  asc: "ચડતો ક્રમ",
  desc: "ઉતરતો ક્રમ",
};

// The names of a list's sorts, from the options of its ગોઠવો filter, for the headers' hover text.
export const sortLabelsOf = <T extends string>(options: FilterOption<T>[]) =>
  Object.fromEntries(options.map((option) => [option.value, option.label])) as Record<T, string>;

const ariaSort = { asc: "ascending", desc: "descending" } as const;

type Props<T extends string> = {
  children: React.ReactNode;
  // The sort a click on this header asks for.
  column: T;
  // Other sorts this header stands for, such as the last-changed date on a header whose cells show both dates.
  alsoFor?: T[];
  // The order the list was loaded in.
  sortBy: T;
  sortOrder: SortOrder;
  onSort: (sortBy: T, sortOrder: SortOrder) => void;
  sortLabels: Record<T, string>;
  disabled?: boolean;
  className?: string;
};

// A table header that sorts the list by its column: a click sorts in ascending order, and a click on the header the
// list is sorted by flips the order. The arrow shows the order of that header; the other sortable headers show a grey
// up-down arrow, navy on hover.
const SortableTableHead = <T extends string>({
  children,
  column,
  alsoFor,
  sortBy,
  sortOrder,
  onSort,
  sortLabels,
  disabled,
  className,
}: Props<T>) => {
  const active = sortBy === column || (alsoFor ?? []).includes(sortBy);
  const order = active ? sortOrder : null;
  const nextBy = active ? sortBy : column;
  const nextOrder: SortOrder = active && sortOrder === "asc" ? "desc" : "asc";

  const Icon =
    order === "asc" ? LuArrowUp : order === "desc" ? LuArrowDown : LuArrowUpDown;

  return (
    <TableHead
      className={cx(tableHeadCellClass, className)}
      aria-sort={order ? ariaSort[order] : "none"}
    >
      {/* The negative margin keeps the label where a plain header has it, in line with the cells below. */}
      <button
        type="button"
        title={`${sortLabels[nextBy]} પ્રમાણે ${orderLabels[nextOrder]}`}
        disabled={disabled}
        onClick={() => onSort(nextBy, nextOrder)}
        className="group relative -mx-1 inline-flex items-center rounded px-1 py-0.5 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] disabled:cursor-wait"
      >
        {children}
        {/* Placed in the cell's padding, outside the label's box, so the column is no wider than with a plain header
            and the table doesn't scroll sideways any sooner. It is still part of the button, so it can be clicked. */}
        <Icon
          aria-hidden="true"
          className={cx(
            "absolute left-full top-1/2 h-3.5 w-3.5 -translate-y-1/2",
            order
              ? "text-[#113F67]"
              : "text-gray-400 group-enabled:group-hover:text-[#113F67]"
          )}
        />
      </button>
    </TableHead>
  );
};

export default SortableTableHead;
